// The page half of browser notifications (the worker decides — data/features/notifications.ts):
// the last on-screen check, drawing, the click, taking them down once read, the permission ask,
// and the unread count in the tab title and favicon.
import { useEffect, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import type { SidebarSnapshot } from '../../../bridge/protocol';
import type { WebNotification } from '../../../data/features/notifications';
import { t } from '../../../i18n/i18n';
import { mediaUrls } from '../../media';
import { snapshotAttention } from '../../models/sidebarModel';
import { NotificationDigest } from '../../../core/notificationDigest';
import { appBadge } from '../../../core/appBadge';
import { VAPID_PUBLIC_KEY, WebPushToken, base64UrlToBytes } from '../../../core/webPushToken';
import './notifications.css';

const DISMISS_KEY = 'satori.notifications.askDismissedAt';
const ASK_AGAIN_MS = 14 * 24 * 3600 * 1000;

function permission(): NotificationPermission | 'unsupported' {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

/** The reader is LOOKING at the page (not merely has it open in a background tab). */
function pageInFront(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

/** The service worker's registration, when the page runs under one (production build): notifications
 *  then go through `showNotification` (actions, and a click works with no tab alive). */
async function swRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return null;
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null;
  } catch {
    return null;
  }
}

/** The apps' two actions (MacAttentionService / Android `replyAction` + `markReadAction`), inline
 *  reply where the platform has a text field on the notification. */
function notificationActions(): Array<Record<string, string>> {
  return [
    { action: 'reply', type: 'text', title: t('mac.notification.reply'), placeholder: t('web.notify.replyPlaceholder') },
    { action: 'markRead', title: t('mac.notification.markRead') },
  ];
}

interface SwNotificationMessage {
  type: 'satori-notification';
  action: 'open' | 'reply' | 'markRead';
  data: { channelId: string; teamId?: string; postId?: string };
  text?: string;
}

let attachedDeviceId: string | null = null;

/**
 * Web Push (PARITY P094/P250, shared/API.md §20.5): with notifications allowed and a service worker, subscribe
 * the browser and attach the subscription to THIS Mattermost session as its device id, so messages and calls
 * arrive with the tab closed (the relay on the chat box sends them). Attached once per page load, again only when
 * the subscription changed. Re-checked whenever the page comes to the front (the permission may have just changed).
 */
async function ensureWebPush(): Promise<void> {
  if (permission() !== 'granted' || !('PushManager' in window)) return;
  const reg = await swRegistration();
  if (!reg) return;
  try {
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(VAPID_PUBLIC_KEY) as BufferSource });
    }
    const p256dh = sub.getKey('p256dh');
    const auth = sub.getKey('auth');
    if (!p256dh || !auth) return;
    const deviceId = WebPushToken.deviceId(sub.endpoint, new Uint8Array(p256dh), new Uint8Array(auth));
    if (!deviceId) {
      console.warn('[web-push] subscription does not fit a Mattermost device id; notifications only while a tab is open');
      return;
    }
    // Once per page load (and again if the subscription changes): a new sign-in is a new session that must
    // carry the device again, so nothing is remembered across loads.
    if (attachedDeviceId === deviceId) return;
    if (await data.call('attachPushDevice', deviceId)) attachedDeviceId = deviceId;
  } catch (error) {
    console.warn('[web-push] subscribe', error);
  }
}

export function useWebNotifications(o: { visibleChannelId: string | null; onOpen(n: WebNotification): void; teamNameFor?(teamId: string): string }): void {
  const latest = useRef(o);
  latest.current = o;
  // channelId → what is showing for it as page Notifications (the no-service-worker path), so reading
  // the conversation takes them down (the apps' rule: a conversation being read has no notifications).
  const shown = useRef(new Map<string, Notification[]>());
  const digest = useRef(new NotificationDigest());

  useEffect(() => {
    void ensureWebPush();
    const again = () => {
      if (document.visibilityState === 'visible') void ensureWebPush();
    };
    document.addEventListener('visibilitychange', again);
    window.addEventListener('focus', again);
    return () => {
      document.removeEventListener('visibilitychange', again);
      window.removeEventListener('focus', again);
    };
  }, []);

  useEffect(
    () =>
      data.onSignal((s) => {
        if (s.kind !== 'notify' || permission() !== 'granted') return;
        const n = s.notification;
        // On screen = the tab in front AND that conversation open (a reply counts: the thread is
        // the same conversation as the channel behind it, as on Apple).
        if (pageInFront() && latest.current.visibleChannelId === n.channelId) return;
        const decision = digest.current.note(n.channelId, n.postId, n.line ?? n.body);
        const body = decision.kind === 'summary' ? decision.body : n.body;
        const icon = mediaUrls.avatar(n.authorId, 0);
        const teamName = n.teamId ? latest.current.teamNameFor?.(n.teamId) ?? '' : latest.current.teamNameFor?.('') ?? '';
        const url = teamName ? `/${encodeURIComponent(teamName)}/pl/${encodeURIComponent(n.postId)}` : '/';
        void (async () => {
          const reg = await swRegistration();
          if (reg) {
            try {
              await reg.showNotification(n.title, {
                body,
                icon,
                badge: '/icons/favicon-64.png',
                // Five per conversation, then ONE summary updated in place (core/notificationDigest).
                tag: decision.tag,
                renotify: decision.kind === 'summary',
                data: { channelId: n.channelId, teamId: n.teamId, postId: n.postId, rootId: n.rootId, url },
                actions: notificationActions(),
              } as NotificationOptions);
              return;
            } catch (error) {
              console.warn('[satori] sw notification', error);
            }
          }
          try {
            const note = new Notification(n.title, { body, icon, tag: decision.kind === 'summary' ? decision.tag : n.channelId, renotify: true } as NotificationOptions);
            note.onclick = () => {
              window.focus();
              latest.current.onOpen(n);
              note.close();
            };
            note.onclose = () => {
              const list = shown.current.get(n.channelId)?.filter((x) => x !== note) ?? [];
              if (list.length) shown.current.set(n.channelId, list);
              else shown.current.delete(n.channelId);
            };
            shown.current.set(n.channelId, [...(shown.current.get(n.channelId) ?? []), note]);
          } catch (error) {
            // Android Chrome forbids `new Notification` (service-worker notifications only).
            console.warn('[satori] notification', error);
          }
        })();
      }),
    [],
  );

  // A click / action on a service-worker notification (public/sw.js): this tab owns the chat, so it
  // answers — opens the conversation, sends the reply through the outbox (nothing typed is lost),
  // or marks the conversation read through the worker — and tells the worker it did.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      const m = e.data as SwNotificationMessage | null;
      if (!m || m.type !== 'satori-notification' || !m.data?.channelId) return;
      const port = e.ports[0];
      const channelId = m.data.channelId;
      const done = () => port?.postMessage({ handled: true });
      if (m.action === 'open') {
        done();
        window.focus();
        latest.current.onOpen({ channelId, teamId: m.data.teamId ?? '', postId: m.data.postId ?? '', rootId: null, title: '', body: '', authorId: '', mention: false });
      } else if (m.action === 'reply' && m.text?.trim()) {
        // Into the CHANNEL, like both twins (the reply is to the conversation, not a thread).
        const pendingId = crypto.randomUUID().replace(/-/g, '');
        void data.call('send', channelId, m.text.trim(), '', pendingId, Date.now(), {});
        void data.call('markChannelRead', channelId).catch(() => undefined);
        withdraw(channelId);
        done();
      } else if (m.action === 'markRead') {
        void data.call('markChannelRead', channelId).catch(() => undefined);
        withdraw(channelId);
        done();
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const withdraw = (channelId: string) => {
    digest.current.withdraw(channelId);
    for (const note of shown.current.get(channelId) ?? []) note.close();
    shown.current.delete(channelId);
    void swRegistration().then(async (reg) => {
      // iOS Safari's registration has no getNotifications (brian.palacio, diego.coss ×4: "is not a function").
      if (typeof reg?.getNotifications !== 'function') return;
      for (const note of await reg.getNotifications()) if ((note.data as { channelId?: string } | null)?.channelId === channelId) note.close();
    });
  };

  // Opening a conversation (or coming back to the tab on top of one) clears its notifications.
  useEffect(() => {
    const clear = () => {
      const id = latest.current.visibleChannelId;
      if (!id || !pageInFront()) return;
      withdraw(id);
    };
    clear();
    window.addEventListener('focus', clear);
    document.addEventListener('visibilitychange', clear);
    return () => {
      window.removeEventListener('focus', clear);
      document.removeEventListener('visibilitychange', clear);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o.visibleChannelId]);
}

/** A quiet card asking for permission — browsers only allow the prompt from a click. */
export function NotificationPermissionCard() {
  const [state, setState] = useState(permission);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return Date.now() - Number(localStorage.getItem(DISMISS_KEY) ?? 0) < ASK_AGAIN_MS;
    } catch {
      return false;
    }
  });
  if (state !== 'default' || dismissed) return null;
  return (
    <div className="notify-card" role="region" aria-label={t('web.notify.askTitle')}>
      <div className="notify-card-text">
        <strong>{t('web.notify.askTitle')}</strong>
        <span>{t('web.notify.askBody')}</span>
      </div>
      <div className="notify-card-actions">
        <button
          type="button"
          className="notify-card-later"
          onClick={() => {
            try {
              localStorage.setItem(DISMISS_KEY, String(Date.now()));
            } catch {
              /* a per-viewer convenience */
            }
            setDismissed(true);
          }}
        >
          {t('web.notify.later')}
        </button>
        <button type="button" className="notify-card-allow" onClick={() => void Notification.requestPermission().then((p) => setState(p))}>
          {t('web.notify.allow')}
        </button>
      </div>
    </div>
  );
}

/** What the tab badges: mentions (a number), else "something unread" (a dot). */
export function badgeCounts(snapshot: SidebarSnapshot | null): { mentions: number; unread: boolean } {
  // The sidebar's own rule (syncParity): member mute AND muted category, silenced teams, your own
  // newest root post, provably-seen channels — so the tab can never disagree with the list.
  const a = snapshotAttention(snapshot);
  return { mentions: a.mentionCount, unread: a.hasUnread };
}

let baseIcon: HTMLImageElement | null = null;

function drawFavicon(mentions: number, unread: boolean): void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"][sizes="32x32"]');
  if (!link) return;
  if (!link.dataset.base) link.dataset.base = link.href;
  if (!mentions && !unread) {
    link.href = link.dataset.base;
    return;
  }
  const paint = () => {
    const c = document.createElement('canvas');
    c.width = c.height = 64;
    const g = c.getContext('2d');
    if (!g || !baseIcon) return;
    g.drawImage(baseIcon, 0, 0, 64, 64);
    const r = mentions ? 17 : 11;
    const x = 64 - r - 1;
    const y = r + 1;
    g.beginPath();
    g.arc(x, y, r + 3, 0, Math.PI * 2);
    g.fillStyle = '#0f1216';
    g.fill();
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fillStyle = mentions ? '#e5484d' : '#4c8dff';
    g.fill();
    if (mentions) {
      g.fillStyle = '#fff';
      g.font = `bold ${mentions > 9 ? 20 : 24}px system-ui, sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText(mentions > 99 ? '99' : String(mentions), x, y + 1);
    }
    link.href = c.toDataURL('image/png');
  };
  if (baseIcon?.complete) paint();
  else {
    baseIcon = new Image();
    baseIcon.onload = paint;
    baseIcon.src = '/icons/favicon-64.png';
  }
}

/** The installed app's icon badge (Badging API; the Mac dock tile's rule — core/appBadge). */
function applyAppBadge(mentions: number, unread: boolean): void {
  const nav = navigator as Navigator & { setAppBadge?(n?: number): Promise<void>; clearAppBadge?(): Promise<void> };
  if (!nav.setAppBadge) return;
  const badge = appBadge(mentions, unread);
  const done = badge.kind === 'count' ? nav.setAppBadge(badge.count) : badge.kind === 'dot' ? nav.setAppBadge() : nav.clearAppBadge?.();
  void done?.catch(() => undefined);
}

/** "(3) Producto y diseño · Satori Chat" — the count reads in a crowded tab strip. */
export function useTabBadge(snapshot: SidebarSnapshot | null, place: string | null): void {
  const { mentions, unread } = badgeCounts(snapshot);
  useEffect(() => {
    const prefix = mentions ? `(${mentions > 99 ? '99+' : mentions}) ` : unread ? '• ' : '';
    document.title = `${prefix}${place ? `${place} · ` : ''}Satori Chat`;
    drawFavicon(mentions, unread);
    applyAppBadge(mentions, unread);
  }, [mentions, unread, place]);
}
